import React, { useState } from 'react';
import { Heart, MessageSquare, Share2, MoreHorizontal, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Post, toggleLike } from '@/db/api';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface PostCardProps {
  post: Post;
  onPostClick?: (post: Post) => void;
}

const PostCard: React.FC<PostCardProps> = ({ post, onPostClick }) => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false); // Should fetch liked status
  const [likesCount, setLikesCount] = useState(post.likes_count);

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!profile) {
      toast.error('请先登录后点赞');
      return;
    }

    try {
      const isLiked = await toggleLike(profile.id, post.id, 'post');
      setLiked(isLiked);
      setLikesCount(prev => (isLiked ? prev + 1 : prev - 1));
    } catch (err) {
      toast.error('操作失败');
    }
  };

  const handlePostClick = (post: Post) => {
    navigate(`/community/post/${post.id}`);
  };

  return (
    <Card 
      className="rounded-xl card-hover border-border/50 hover:border-primary/20 cursor-pointer overflow-hidden transition-all duration-300"
      onClick={() => handlePostClick(post)}
    >
      <CardHeader className="p-4 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8 border border-primary/10">
              <AvatarImage src={post.profiles?.avatar_url} />
              <AvatarFallback className="bg-primary/10 text-primary">
                <User className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="text-sm font-bold text-foreground">
                {post.profiles?.username || '用户'}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: zhCN })}
              </div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-muted-foreground"
            onClick={(e) => { e.stopPropagation(); }}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-2">
        <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed mb-4">
          {post.content}
        </p>
        
        {post.images && post.images.length > 0 && (
          <div className="grid grid-cols-2 gap-2 rounded-lg overflow-hidden mb-4">
            {post.images.slice(0, 4).map((img, i) => (
              <div key={i} className="aspect-video relative overflow-hidden bg-muted">
                <img src={img} alt="post" className="w-full h-full object-cover" />
                {i === 3 && post.images.length > 4 && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white font-bold">
                    +{post.images.length - 4}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {post.tags?.map((tag) => (
            <span key={tag} className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
              #{tag}
            </span>
          ))}
        </div>
      </CardContent>
      <CardFooter className="p-4 pt-2 border-t border-border/50 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            className={`gap-1.5 h-8 ${liked ? 'text-accent-paid' : 'text-muted-foreground'}`}
            onClick={handleLike}
          >
            <Heart className={`h-4 w-4 ${liked ? 'fill-current' : ''}`} />
            <span className="text-xs font-bold">{likesCount}</span>
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="gap-1.5 h-8 text-muted-foreground"
            onClick={(e) => { e.stopPropagation(); }}
          >
            <MessageSquare className="h-4 w-4" />
            <span className="text-xs font-bold">{post.comments_count}</span>
          </Button>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-muted-foreground"
          onClick={(e) => { e.stopPropagation(); }}
        >
          <Share2 className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PostCard;